import { useCallback, useEffect, useRef, useState } from "react";

interface UsePollingOptions {
  callback: () => Promise<void> | void;
  delay: number;
  startImmediately?: boolean;
}

interface UsePollingResult {
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  isPending: boolean;
}

const usePolling = (
  callback: () => Promise<void> | void,
  delay: number,
  startImmediately: boolean = true
): UsePollingResult => {
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const [isPending, setIsPending] = useState<boolean>(false);
  const savedCallback = useRef<() => Promise<void> | void>(callback);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeCallRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const currentDelayRef = useRef<number>(delay);
  const startImmediatelyRef = useRef<boolean>(startImmediately);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    // Update the ref when the prop changes
    startImmediatelyRef.current = startImmediately;
  }, [startImmediately]);

  useEffect(() => {
    if (currentDelayRef.current !== delay) {
      currentDelayRef.current = delay;
      if (isPolling && !activeCallRef.current && isMountedRef.current) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        timeoutRef.current = setTimeout(tick, delay);
      }
    }
  }, [delay]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      activeCallRef.current = false;
    };
  }, []);

  const tick = useCallback(async () => {
    if (!isPolling || !isMountedRef.current || activeCallRef.current) return;
    try {
      abortControllerRef.current = new AbortController();
      activeCallRef.current = true;
      setIsPending(true);
      await savedCallback.current();
    } catch (error) {
      if (!isMountedRef.current) return;
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Polling request was aborted");
      } else {
        console.error("Error in polling callback:", error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsPending(false);
        activeCallRef.current = false;
        if (isPolling) {
          timeoutRef.current = setTimeout(tick, currentDelayRef.current);
        }
      }
    }
  }, [isPolling]);

  const startPolling = useCallback(() => {
    if (isPolling) return;
    setIsPolling(true);
  }, [isPolling]);

  const stopPolling = useCallback(() => {
    if (!isPolling) return;
    setIsPolling(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPending(false);
  }, [isPolling]);

  // Effect to handle changes in isPolling state and startImmediately prop
  useEffect(() => {
    if (isPolling && !activeCallRef.current && isMountedRef.current) {
      // Clear any existing timeout to ensure we don't have multiple timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (startImmediatelyRef.current) {
        void tick();
      } else {
        timeoutRef.current = setTimeout(tick, currentDelayRef.current);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isPolling, tick, startImmediately]); // Include startImmediately in dependencies

  return {
    isPolling,
    startPolling,
    stopPolling,
    isPending,
  };
};

export default usePolling;
